/**
 * ListAdder — edit a lookup list (customer statuses, tags…): add, delete, reorder.
 * Opened from the side menu's "הגדרות" entries: setShowListAdder(true, 'customerStatus').
 * Mounted once by ui/AppLayout.
 *
 * Data (unchanged from the pre-redesign version):
 *   getListFromDB("id,val,location", [{listName}], listName)   on open (items without a
 *                                                              location get their index saved)
 *   addItemToList(listName, value)                             add
 *   deleteFromList(id, listName)                               delete (now asks to confirm first)
 *   updateListItemLocation(id, location)                       move up / down (swaps two rows)
 */
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';

import * as actionsCreators from './../../../actions/index.js';
import { Modal, Button, Input, Tooltip, EmptyState, Popconfirm } from '../../../ui';
import classes from './listAdder.css';

// Friendly titles for the list names used by the API
const LIST_TITLES = {
  customerStatus: 'סטטוסי לקוח',
  eventsType: 'טאגים',
};

const ListAdder = (props) => {
  const { showListAdder, listAdderListName } = props;
  const [filter, setFilter] = useState('');
  const [newItem, setNewItem] = useState('');
  const [list, setList] = useState([]);
  const wasOpen = useRef(false);

  const fetchList = (listName) => props.getListFromDB('id,val,location', [{ key: 'listName', val: [listName] }], listName);

  // On open: load the list; rows without a saved location get their index saved (as before).
  useEffect(() => {
    if (showListAdder && !wasOpen.current) {
      (async () => {
        let items = await fetchList(listAdderListName);
        items = (items || []).map((item, i) => {
          if (item.location === null) {
            item.location = i;
            props.updateListItemLocation(item.id, i);
          }
          return item;
        });
        setList(items);
      })();
    }
    wasOpen.current = showListAdder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showListAdder, listAdderListName]);

  const refresh = async () => setList((await fetchList(listAdderListName)) || []);

  const handleClose = () => {
    setFilter('');
    setNewItem('');
    setList([]);
    props.setShowListAdder(false, '');
  };

  const onAddHandler = async () => {
    if (newItem === '') return;
    const res = await props.addItemToList(listAdderListName, newItem.trim());
    if (res === 'err') props.showAlert('error!');
    else {
      props.showAlert('השורה נכנסה בהצלחה');
      setNewItem('');
      refresh();
    }
  };

  const onDeleteHandler = async (id) => {
    await props.deleteFromList(id, listAdderListName);
    refresh();
  };

  // Swap row i with its neighbour and save both locations.
  const itemMove = (direction, id, i) => {
    let newList = [...list];
    const j = direction === 'up' ? i - 1 : i + 1;
    props.updateListItemLocation(id, j);
    props.updateListItemLocation(newList[j].id, i);
    newList[i] = { ...newList[i], location: j };
    newList[j] = { ...newList[j], location: i };
    newList = newList.sort((a, b) => a.location - b.location);
    setList(newList);
  };

  const visible = list
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => !(filter !== '' && item.val && !item.val.includes(filter)));

  return (
    <Modal
      open={showListAdder}
      onCancel={handleClose}
      width={560}
      title={`ניהול ${LIST_TITLES[listAdderListName] || listAdderListName || ''}`}
      footer={<Button onClick={handleClose}>סגור</Button>}
    >
      <div className={classes.body}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="חיפוש…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        <ul className={classes.list}>
          {visible.length === 0 ? (
            <li>
              <EmptyState compact title={filter ? 'אין תוצאות' : 'הרשימה ריקה'} />
            </li>
          ) : (
            visible.map(({ item, i }) => (
              <li key={item.id} className={classes.row}>
                <span className={classes.value}>{item.val}</span>
                <span className={classes.tools}>
                  <Tooltip title="הזז למעלה">
                    <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => itemMove('up', item.id, i)} />
                  </Tooltip>
                  <Tooltip title="הזז למטה">
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={i === list.length - 1}
                      onClick={() => itemMove('down', item.id, i)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title={`למחוק את "${item.val}"?`}
                    okText="מחק"
                    okButtonProps={{ danger: true }}
                    cancelText="ביטול"
                    onConfirm={() => onDeleteHandler(item.id)}
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="מחק" />
                  </Popconfirm>
                </span>
              </li>
            ))
          )}
        </ul>

        <div className={classes.addRow}>
          <Input
            placeholder="ערך חדש"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onPressEnter={onAddHandler}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddHandler} disabled={!newItem.trim()}>
            הוסף
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    showListAdder: state.ShowModels.showListAdder,
    listAdderListName: state.ShowModels.listAdderListName,
  };
};

export default connect(mapStateToProps, actionsCreators)(ListAdder);
